import SeatChangeConfirmModal from 'components/seat-change-confirm-modal';
import { useState } from 'react';
import useToast from 'hooks/useToast';
import { ISeatChangeTerms } from 'redux-store/api/billing/billing.types';
import { useGetCurrentPaymentPlanQuery, useLazyGetInviteSeatChangeTermsQuery } from 'redux-store/api/billing';

type SeatAction = () => unknown | Promise<unknown>;

export const useSeatChangeGuard = () => {
  const { data: currentPlan, isFetching: isPlanFetching, isError: isPlanError } = useGetCurrentPaymentPlanQuery();
  const [pending, setPending] = useState<{
    targetSeatCount: number;
    action: SeatAction;
    terms?: ISeatChangeTerms;
  } | null>(null);

  const showToast = useToast();
  const [getInviteTerms] = useLazyGetInviteSeatChangeTermsQuery();
  const runAction = async (action: SeatAction) => {
    try { await action(); } catch (error: unknown) {
      showToast({ type: 'error', message: 'Unable to complete this action. Please try again.' });
    }
  };

  const isSeatBased = currentPlan?.seatBasedPricing === true;
  const currentSeatCount = currentPlan?.activeUsersAmount ?? 0;

  const requirePlan = (): boolean => {
    if (currentPlan && !isPlanFetching && !isPlanError) return true;
    showToast({ type: 'error', message: 'Billing information is unavailable. Please try again.' });
    return false;
  };

  const requestSeatChange = (targetSeatCount: number, action: SeatAction) => {
    if (!requirePlan()) return;
    if (!isSeatBased || targetSeatCount === currentSeatCount) {
      void runAction(action);
      return;
    }

    setPending({ targetSeatCount, action });
  };

  const requestInviteChange = async (email: string, action: SeatAction) => {
    if (!requirePlan()) return;
    if (!isSeatBased) { await runAction(action); return; }
    try {
      const terms = await getInviteTerms({ email }, false).unwrap();
      if (terms) setPending({ targetSeatCount: terms.newSeatCount, action, terms });
      else await runAction(action);
    } catch (error: unknown) {
      showToast({ type: 'error', message: 'Unable to load billing terms. Please try again.' });
    }
  };

  const seatChangeModal = pending ? (
    <SeatChangeConfirmModal
      targetSeatCount={pending.targetSeatCount}
      initialTerms={pending.terms}
      onConfirmed={pending.action}
      onClose={() => setPending(null)}
    />
  ) : null;

  return { requestInviteChange, requestSeatChange, seatChangeModal, currentSeatCount, isSeatBased };
};
